import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, BarChart3, ClipboardList, Check, ArrowRight, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SeoHead } from '@/components/SeoHead';
import { Logo } from '@/components/Logo';
import { COACH_TIER_LIMITS } from '@/lib/stripeConfig';

const VALUE_PROPS = [
  { icon: BookOpen, title: 'Assign lessons', body: 'Pick sport-specific lessons, videos and articles and assign them to your whole team in a few taps.' },
  { icon: BarChart3, title: 'Track progress', body: 'See who started, who finished and who needs a nudge — per player and per lesson.' },
  { icon: ClipboardList, title: 'Build your playbook', body: 'Group your own videos and articles into lessons your players can work through between practices.' },
];

const EXAMPLES = [
  { sport: 'Soccer', team: 'U12 Example FC', quote: 'An example of what a coach might say once real reviews come in.' },
  { sport: 'Basketball', team: 'Example Middle School', quote: 'Placeholder quote — we will only show real coaches with their permission.' },
  { sport: 'Football', team: 'Example Youth Flag', quote: 'Sample card to preview this section. Not a real testimonial.' },
];

const START = '/auth/coach';

export default function CoachesLanding() {
  const free = COACH_TIER_LIMITS.free;
  const [params] = useSearchParams();
  // Remember a referral code (?ref=) so coach signup can attribute it.
  useEffect(() => {
    const ref = (params.get('ref') || '').replace(/[^a-z0-9]/gi, '').slice(0, 32);
    if (ref.length >= 6) localStorage.setItem('sportara-coach-ref', ref.toLowerCase());
  }, [params]);
  return (
    <div className="min-h-screen premium-page-bg pb-24 sm:pb-0">
      <SeoHead
        title="Coaches Hub — Assign Lessons & Track Your Players | Sportara"
        description="Your players learn the game. You track everything. Assign sport-specific lessons to your youth team and see who completed them. Free during beta."
        path="/coaches"
      />
      <header className="max-w-5xl mx-auto flex items-center justify-between px-4 py-4">
        <Link to="/" className="flex items-center gap-2" aria-label="Sportara home">
          <Logo className="w-9 h-9" alt="" /><span className="font-display font-bold text-lg">Sportara</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm"><Link to="/auth">Sign in</Link></Button>
          <Button asChild size="sm" className="hidden sm:inline-flex"><Link to={START}>Start free</Link></Button>
        </div>
      </header>

      <main>
        <section className="max-w-5xl mx-auto px-4 pt-10 pb-14 text-center">
          <p className="inline-block rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-display font-bold mb-4">Coaches Hub · Free during beta</p>
          <h1 className="font-display font-bold text-4xl sm:text-6xl leading-tight max-w-3xl mx-auto">
            Your players learn the game. You track everything.
          </h1>
          <p className="mt-5 text-muted-foreground max-w-xl mx-auto">
            Assign short, sport-specific lessons to your team, share one join link, and see exactly who did the work.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="h-14 px-8 font-display font-bold"><Link to={START}>Start free <ArrowRight className="ml-2 w-4 h-4" /></Link></Button>
            <Button asChild size="lg" variant="outline" className="h-14 px-8 font-display font-bold"><a href="#demo">See it in action</a></Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">No credit card needed.</p>
        </section>

        <section aria-labelledby="value-heading" className="max-w-5xl mx-auto px-4 pb-16">
          <h2 id="value-heading" className="sr-only">What you can do</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-6">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-primary" /></div>
                <h3 className="font-display font-bold text-lg">{title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="demo" aria-labelledby="demo-heading" className="max-w-5xl mx-auto px-4 pb-16 scroll-mt-20">
          <h2 id="demo-heading" className="font-display font-bold text-3xl text-center">See it in action</h2>
          <p className="text-center text-sm text-muted-foreground mt-2">An illustrated walkthrough with example data.</p>
          <div className="mt-8 mx-auto max-w-md rounded-3xl border border-border bg-card p-5 shadow-xl overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-bold">Example Team · Soccer</span>
              <span className="text-[10px] uppercase tracking-wider rounded bg-muted px-2 py-0.5 text-muted-foreground">Example</span>
            </div>
            <div className="space-y-3">
              <div className="rounded-xl bg-primary/10 p-3 text-sm font-display font-bold demo-step demo-step-1">1. Assign "Defending 1v1"</div>
              {['Player A', 'Player B', 'Player C'].map((p, i) => (
                <div key={p} className={`rounded-xl border border-border p-3 demo-step demo-step-${i + 2}`}>
                  <div className="flex justify-between text-sm"><span>{p}</span><span className="text-muted-foreground">{['Done', 'In progress', 'Not started'][i]}</span></div>
                  <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full bg-primary demo-bar demo-bar-${i}`} /></div>
                </div>
              ))}
              <div className="rounded-xl bg-accent/15 p-3 text-sm demo-step demo-step-5">2. See who finished — and who needs a nudge</div>
            </div>
          </div>
        </section>

        <section id="pricing" aria-labelledby="pricing-heading" className="max-w-5xl mx-auto px-4 pb-16">
          <h2 id="pricing-heading" className="font-display font-bold text-3xl text-center">Simple pricing</h2>
          <p className="text-center text-sm text-muted-foreground mt-2 max-w-lg mx-auto">There are two coach plans today. "Free" is our Basic plan — there is no separate paid Basic tier.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 max-w-3xl mx-auto">
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col">
              <h3 className="font-display font-bold text-xl">Free <span className="text-sm text-muted-foreground font-normal">(Basic)</span></h3>
              <p className="font-display font-bold text-3xl mt-2">$0</p>
              <ul className="mt-4 space-y-2 text-sm flex-1">
                {[`${free.teams} team`, `Up to ${free.assignments} active lesson assignments`, 'Player progress tracking', 'Shareable join link'].map(f => (
                  <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Button asChild className="mt-6 font-display font-bold"><Link to={START}>Start free</Link></Button>
            </div>
            <div className="rounded-2xl border-2 border-primary/40 bg-card p-6 flex flex-col">
              <h3 className="font-display font-bold text-xl">Coach Pro</h3>
              <p className="font-display font-bold text-3xl mt-2">$9.99<span className="text-sm text-muted-foreground font-normal">/month</span></p>
              <ul className="mt-4 space-y-2 text-sm flex-1">
                {['Unlimited teams', 'Unlimited assignments', 'Team-wide analytics'].map(f => (
                  <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Button asChild variant="outline" className="mt-6 font-display font-bold"><Link to={START}>Start free</Link></Button>
              <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" /> Upgrade anytime from your dashboard once you've set up a team.</p>
            </div>
          </div>
        </section>

        <section aria-labelledby="examples-heading" className="max-w-5xl mx-auto px-4 pb-20">
          <h2 id="examples-heading" className="font-display font-bold text-3xl text-center">Join coaches already using Sportara</h2>
          <p className="text-center text-sm text-muted-foreground mt-2">We're in private beta. The cards below are placeholders, not real coaches.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {EXAMPLES.map(c => (
              <figure key={c.team} className="relative rounded-2xl border border-dashed border-border bg-card/60 p-5">
                <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider rounded bg-muted px-2 py-0.5 text-muted-foreground font-bold">Example</span>
                <p className="text-xs text-primary font-display font-bold">{c.sport}</p>
                <p className="font-display font-bold mt-1">{c.team}</p>
                <blockquote className="text-sm text-muted-foreground mt-3 italic">"{c.quote}"</blockquote>
              </figure>
            ))}
          </div>
        </section>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur p-3 sm:hidden">
        <Button asChild className="w-full h-12 font-display font-bold"><Link to={START}>Start free — no credit card needed</Link></Button>
      </div>
    </div>
  );
}
